
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { HelmetProvider } from "react-helmet-async";
import { register as registerServiceWorker } from "./registerServiceWorker";
import { Suspense, lazy } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import RoleProtectedRoute from "@/components/auth/RoleProtectedRoute";
import "./App.css";
import "@fontsource/bubblegum-sans";
import "@fontsource/dancing-script";
import "./components/tests/TestPrintWatermark.css";

// Import critical routes directly to prevent loading issues
import Auth from "./pages/Auth";
import AdminQuestionGenerator from "./pages/AdminQuestionGenerator";

// Other pages can still use lazy loading
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SubjectSelection = lazy(() => import("./pages/SubjectSelection"));
const TestPage = lazy(() => import("./pages/TestPage"));
const ResultsPage = lazy(() => import("./pages/ResultsPage"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const AdminQuestions = lazy(() => import("./pages/AdminQuestions"));
const AdminAILogs = lazy(() => import("./pages/AdminAILogs"));
const AdminReports = lazy(() => import("./pages/AdminReports"));
const AdminSubjects = lazy(() => import("./pages/AdminSubjects"));
const AdminCategorySuggestions = lazy(() => import("./pages/AdminCategorySuggestions"));
const Analysis = lazy(() => import("./pages/Analysis"));
const AdminEmailTemplates = lazy(() => import("./pages/AdminEmailTemplates"));
const AdminEmailCampaigns = lazy(() => import("./pages/AdminEmailCampaigns"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const WritingPad = lazy(() => import("./pages/WritingPad"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000
    }
  }
});

// Create a helmet context to ensure proper functioning
const helmetContext = {};

function App() {
  registerServiceWorker();

  return (
    <HelmetProvider context={helmetContext}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <SettingsProvider>
              <div className="flex flex-col min-h-screen">
                <Header />
                <main className="flex-1">
                  <Suspense fallback={<div className="container mx-auto p-4 flex justify-center items-center h-[70vh]">Loading...</div>}>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/terms" element={<TermsAndConditions />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/grade/:grade" element={<SubjectSelection />} />
                      <Route path="/grade/:grade/subject/:subject" element={<TestPage />} />
                      <Route path="/grade/:grade/subject/:subject/category/:category" element={<TestPage />} />
                      <Route path="/results/:id" element={<ResultsPage />} />
                      <Route path="/analysis" element={<Analysis />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/writing-pad" element={<WritingPad />} />
                      <Route path="/grade/:grade/writing-pad" element={<WritingPad />} />
                      <Route 
                        path="/admin/questions" 
                        element={
                          <RoleProtectedRoute requiredRole="admin">
                            <AdminQuestions />
                          </RoleProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/admin/logs" 
                        element={
                          <RoleProtectedRoute requiredRole="admin">
                            <AdminAILogs />
                          </RoleProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/admin/generator" 
                        element={
                          <RoleProtectedRoute requiredRole="admin">
                            <AdminQuestionGenerator />
                          </RoleProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/admin/reports" 
                        element={
                          <RoleProtectedRoute requiredRole="admin">
                            <AdminReports />
                          </RoleProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/admin/subjects" 
                        element={
                          <RoleProtectedRoute requiredRole="admin">
                            <AdminSubjects />
                          </RoleProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/admin/suggestions" 
                        element={
                          <RoleProtectedRoute requiredRole="admin">
                            <AdminCategorySuggestions />
                          </RoleProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/admin/email-templates" 
                        element={
                          <RoleProtectedRoute requiredRole="admin">
                            <AdminEmailTemplates />
                          </RoleProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/admin/email-campaigns" 
                        element={
                          <RoleProtectedRoute requiredRole="admin">
                            <AdminEmailCampaigns />
                          </RoleProtectedRoute>
                        } 
                      />
                      <Route path="/unsubscribe" element={<Unsubscribe />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </main>
                <Footer />
              </div>
              <Toaster />
              <SonnerToaster position="top-right" />
            </SettingsProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
